import { Injectable, Signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { WlcmQueryParams } from '@wlcm/common';

import { WlcmPaginatedData } from '@wlcm/common';
import { ApiError } from '@core/models/http.models';
import { ActionResponseHandlerService } from '@store/services';

import { LocationsSelectors } from './location.selector';
import { Location } from '@core/models/entities/location.models';

import {
  LoadLocationsPaginatedActions,
  LoadLocationsPaginatedMoreActions,
  ResetPartial,
  AddLocation,
  PatchLocationAction,
  DeleteLocation,
} from './location.actions';
import { LocationPayload } from '@core/models/entities/location.models';

@Injectable({ providedIn: 'root' })
export class LocationFacade {
  readonly loading: Signal<boolean> = this.store.selectSignal(LocationsSelectors.isFetching);

  readonly all: Signal<Location[]> = this.store.selectSignal(LocationsSelectors.all);

  readonly entities: Signal<Dictionary<Location>> = this.store.selectSignal(LocationsSelectors.entities);

  readonly location: Signal<Location | null> = this.store.selectSignal(LocationsSelectors.location);

  readonly queryParams: Signal<WlcmQueryParams> = this.store.selectSignal(LocationsSelectors.queryParams);

  readonly paginatedData: Signal<WlcmPaginatedData<Location> | null> = this.store.selectSignal(
    LocationsSelectors.paginatedData
  );

  readonly isLoadingPaginated: Signal<boolean> = this.store.selectSignal(LocationsSelectors.isFetchingPaginated);

  constructor(
    private readonly store: Store,
    private readonly actionHandler: ActionResponseHandlerService
  ) {}

  loadPaginated(params: Partial<WlcmQueryParams> = {}): Observable<WlcmPaginatedData<Location> | ApiError> {
    this.store.dispatch(LoadLocationsPaginatedActions.init({ payload: params }));
    return this.actionHandler.handle(LoadLocationsPaginatedActions);
  }

  loadMorePaginated(): Observable<WlcmPaginatedData<Location> | ApiError | null> {
    const paginatedData: WlcmPaginatedData<Location> | null = this.paginatedData();
    if (!paginatedData || paginatedData.currPage >= paginatedData.totalPages) return of(null);
    this.store.dispatch(LoadLocationsPaginatedMoreActions.init());
    return this.actionHandler.handle(LoadLocationsPaginatedMoreActions);
  }

  add(payload: LocationPayload): Observable<Location> {
    this.store.dispatch(AddLocation.init({ payload: { id: null, data: payload } }));
    return this.actionHandler.handle(AddLocation);
  }

  patch(id: string | null, data: Partial<LocationPayload>): Observable<Location> {
    this.store.dispatch(PatchLocationAction.init({ payload: { id, data } }));
    return this.actionHandler.handle(PatchLocationAction);
  }

  delete(id: string): void {
    this.store.dispatch(DeleteLocation.init({ payload: { id: id } }));
  }

  reset(): void {
    this.store.dispatch(ResetPartial());
  }
}
